import { IUser } from '@tacans/shared-marketplace-layer';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect } from 'react';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import { ShareModal } from 'src/features/common/app/modals/share-modal';
import { ProfileWalletsContainerProps } from 'src/features/common/profile/containers/profile-wallets-container/types';
import { useAvatarAssets } from 'src/features/common/profile/hooks/use-avatar-assets';
import { useBannerAssets } from 'src/features/common/profile/hooks/use-banner-assets';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useFetchUserWallets } from 'src/features/common/wallet/hooks/use-fetch-user-wallets';
import { EntityLayout } from 'src/ui/white-labels/ocean/layouts/entity-layout';

const ProfileWalletsContainer = dynamic<ProfileWalletsContainerProps>(
    () =>
        import('src/features/white-labels/ocean/profile/containers/profile-wallets-container').then(
            (d) => d.ProfileWalletsContainer
        ) as any,
    {
        ssr: false,
    }
);

export const ProfileDetailsContainer: React.VFC<{ profile: IUser }> = ({ profile }) => {
    const { wallets, fetchWallets } = useFetchUserWallets();
    const { projectName } = useUniverse();
    const profileName = useDisplayName(profile, true);

    const avatarAssets = useAvatarAssets({ profile });
    const bannerAssets = useBannerAssets({ profile });

    useEffect(() => {
        fetchWallets(profile.login);
    }, [fetchWallets, profile?.login]);

    const openShareModal = useCallback(() => {
        ShareModal.open({
            previewImg: profile.avatarAssets?.webps?.mediumUrl!,
            title: profileName,
            subTitle: projectName,
            url: generatePath(RouteE.PROFILE, { login: profile.login }),
        });
    }, [profile, profileName, projectName]);

    return (
        <EntityLayout>
            <EntityLayout.Banner url={bannerAssets.retina} />
            <EntityLayout.Container>
                <EntityLayout.Avatar url={avatarAssets.retina} />
                {/* @ts-ignore */}
                <ProfileWalletsContainer wallets={wallets} />
                <EntityLayout.Socials socialNetworks={profile.socialNetworks} openShareModal={openShareModal} />
            </EntityLayout.Container>
            <EntityLayout.Title title={profileName} />
            <EntityLayout.Description description={profile.aboutMe} />
        </EntityLayout>
    );
};
